import { Product, ProductSlug, SkuOption } from "@/api/product-api";
import { CartUtil } from "@/utils/CartUtil";
import { getAvailableColors } from "@/utils/product-colors";

/**
 * 글러브 **사이즈 세트**(S·M·L·XL 각 1켤레) — 판정을 한 곳에 둔다.
 *
 * 세트는 데이터상 별도 번들 제품(`tactglove-dk3-bundle`)이지만 화면에서는 글러브 PDP 의
 * 사이즈 칩 하나(`all-size`)로 고른다. 「지금 세트를 고를 수 있나」를 묻는 자리가 셋이라
 * (칩 활성 · `?bundle=` 진입 시 첫 선택 · 구조화 데이터/GMC 피드) 각자 계산하면 갈린다 —
 * 칩은 매진인데 페이지는 세트를 골라 둔 상태, 또는 피드는 세트가인데 랜딩은 단품인 상태.
 */
export const GLOVE_SET_SIZE = "all-size";

/** 세트를 이루는 사이즈 — 사이즈 칩 순서이기도 하다. */
export const GLOVE_SIZES = ["small", "medium", "large", "x-large"] as const;

/**
 * 그 색의 네 사이즈가 **모두** 재고 있는가 — 세트 칩 활성 조건.
 *
 * 세트 제품 자체의 재고가 아니라 **단품 SKU 재고**로 본다(종전 `TactGloveOptions` 의
 * `canSelectGloveSet` 그대로). 세트는 단품 네 켤레를 묶어 출고하기 때문이다.
 */
export function hasEveryGloveSize(
  skuOptions: SkuOption[],
  productSlug: string,
  color: string,
): boolean {
  return GLOVE_SIZES.every((size) => {
    const sku = `${productSlug}-${color}-${size}`;
    return (skuOptions.find((o) => o.sku === sku)?.stock ?? 0) > 0;
  });
}

/**
 * `?bundle=<글러브 세트 slug>` 로 들어온 글러브 PDP 가 **세트를 골라 둔 채 열리는가** —
 * 고르면 그 세트 제품, 아니면 `null`.
 *
 * 번들 상세 주소(`/shop/buy-tactsuit/tactglove-dk3-bundle/`)는 `BUNDLE_BASE_BY_SLUG` 로
 * 글러브 PDP `?bundle=` 에 이관된다. 사용자는 이미 세트를 골라서 온 것이라 세트를 선택해
 * 두되, 고를 수 없으면(네 사이즈 중 하나라도 품절 · 세트 제품이 카탈로그에 없음) 아무것도
 * 고르지 않은 일반 진입과 같게 연다 — 매진 칩이 골라진 채로 두지 않는다.
 *
 * ⚠️ 일반 진입(`?bundle=` 없음)에서는 **아무 사이즈도 미리 고르지 않는다**(잘못된 사이즈 구매
 * 방지). 세트를 골라 두는 건 **주소가 세트를 말할 때만**이다 — 세트는 네 사이즈가 다 들어 있어
 * 미리 골라도 사이즈를 잘못 고를 위험이 없다.
 *
 * `color` 가 없으면 재고 있는 첫 색(페이지 기본 색과 같은 규칙).
 */
export function resolvePreselectedGloveSet({
  product,
  products,
  bundleSlug,
  color,
}: {
  product: Product;
  products: Product[];
  bundleSlug: string | null | undefined;
  color?: string | null;
}): Product | null {
  const own = CartUtil.getGloveBundleSlug(product.slug as ProductSlug);
  if (!own || bundleSlug !== own) return null;
  const set = products.find((p) => p.slug === own);
  if (!set) return null;
  const c =
    color ||
    getAvailableColors(product.skuOptions).find((x) => x.available)?.color;
  if (!c) return null;
  return hasEveryGloveSize(product.skuOptions, product.slug, c) ? set : null;
}
